import { useState, useEffect, useCallback } from 'react'
import { Globe, Plus, RefreshCw, Trash2, ChevronRight, ChevronDown, FileText, Edit3, X, Check, Loader2, Copy, CheckCheck } from 'lucide-react'
import { countryGuidesApi, type CountryGuideProfile } from '@api/client'
import { COUNTRY_CODES } from '../countryCodes'
import { useAuthStore } from '@store/authStore'

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pending', color: 'text-[var(--text-muted)]', bgColor: 'bg-[var(--bg-tertiary)]' },
  generating: { label: 'Generating…', color: 'text-blue-700 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-500/10' },
  generated: { label: 'Generated', color: 'text-green-700 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-500/10' },
  error: { label: 'Error', color: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-500/10' },
}

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
      {config.label}
    </span>
  )
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '—'
  // D1 returns 'YYYY-MM-DD HH:MM:SS' — normalise to ISO
  const iso = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Inline formatting: **bold**, *italic*, `code`
function formatInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index))
    if (match[2]) parts.push(<strong key={match.index} className="font-semibold text-[var(--text-primary)]">{match[2]}</strong>)
    else if (match[3]) parts.push(<em key={match.index}>{match[3]}</em>)
    else if (match[4]) parts.push(<code key={match.index} className="bg-[var(--bg-tertiary)] px-1 rounded text-[var(--text-primary)]">{match[4]}</code>)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return parts
}

// Markdown renderer for guide content
function renderGuideMarkdown(md: string) {
  const lines = md.split('\n')
  const elements: React.ReactNode[] = []
  let blockquoteLines: string[] = []

  const flushBlockquote = () => {
    if (!blockquoteLines.length) return
    elements.push(
      <blockquote key={`bq-${elements.length}`} className="border-l-2 border-primary pl-3 py-1 text-xs text-[var(--text-secondary)] italic my-2">
        {blockquoteLines.map((l, i) => <span key={i}>{formatInline(l)}{i < blockquoteLines.length - 1 && <br />}</span>)}
      </blockquote>
    )
    blockquoteLines = []
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Blockquote continuation
    if (line.startsWith('> ')) {
      blockquoteLines.push(line.slice(2))
      continue
    }
    if (blockquoteLines.length) flushBlockquote()

    // Headings
    if (line.startsWith('#### ')) {
      elements.push(<h5 key={i} className="text-xs font-semibold text-[var(--text-primary)] mt-2 mb-1">{formatInline(line.slice(5))}</h5>)
    } else if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="text-sm font-medium text-[var(--text-primary)] mt-3 mb-1">{formatInline(line.slice(4))}</h4>)
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="text-sm font-semibold text-[var(--text-primary)] mt-4 mb-2">{formatInline(line.slice(3))}</h3>)
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="text-base font-bold text-[var(--text-primary)] mt-5 mb-2">{formatInline(line.slice(2))}</h2>)
    }
    // Numbered list
    else if (/^\d+\.\s/.test(line)) {
      const text = line.replace(/^\d+\.\s+/, '')
      elements.push(<p key={i} className="text-xs text-[var(--text-secondary)] ml-2 mb-1 pl-2">{line.match(/^\d+/)?.[0]}. {formatInline(text)}</p>)
    }
    // Bullet with bold label
    else if (line.startsWith('- **') || line.startsWith('  - **')) {
      const indent = line.startsWith('  ') ? 'ml-6' : 'ml-2'
      const trimmed = line.replace(/^\s+-\s+/, '')
      elements.push(<div key={i} className={`text-xs text-[var(--text-muted)] ${indent} mb-1`}>• {formatInline(trimmed)}</div>)
    }
    // Regular bullet
    else if (line.startsWith('- ') || line.startsWith('  - ')) {
      const indent = line.startsWith('  ') ? 'ml-6' : 'ml-2'
      const text = line.replace(/^\s+-\s+/, '')
      elements.push(<p key={i} className={`text-xs text-[var(--text-muted)] ${indent} mb-1`}>• {formatInline(text)}</p>)
    }
    // Horizontal rule
    else if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} className="border-[var(--border-color)] my-3" />)
    }
    // Non-empty paragraph
    else if (line.trim()) {
      elements.push(<p key={i} className="text-xs text-[var(--text-secondary)] mb-1">{formatInline(line)}</p>)
    }
  }
  if (blockquoteLines.length) flushBlockquote()

  return elements
}

// ─── Detail View ──────────────────────────────────────────────────────

function CountryGuideDetail({ code, onBack }: { code: string; onBack: () => void }) {
  const [profile, setProfile] = useState<CountryGuideProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [editingPrompt, setEditingPrompt] = useState(false)
  const [promptText, setPromptText] = useState('')
  const [defaultPrompt, setDefaultPrompt] = useState('')
  const [savingPrompt, setSavingPrompt] = useState(false)
  const [contentExpanded, setContentExpanded] = useState(true)
  const [copied, setCopied] = useState(false)
  const [promptCopied, setPromptCopied] = useState(false)
  const user = useAuthStore(s => s.user)

  const load = useCallback(async () => {
    setLoading(true)
    const [profileRes, defaultRes] = await Promise.all([
      countryGuidesApi.get(code),
      countryGuidesApi.getDefaultPrompt(),
    ])
    if (profileRes.success && profileRes.data) {
      setProfile(profileRes.data)
      setPromptText(profileRes.data.custom_prompt || '')
    }
    if (defaultRes.success && defaultRes.data) {
      setDefaultPrompt(defaultRes.data.prompt)
    }
    setLoading(false)
  }, [code])

  useEffect(() => { load() }, [load])

  const handleGenerate = async () => {
    if (!profile) return
    setGenerating(true)
    const res = await countryGuidesApi.generate(code, user?.email, user?.name)
    if (res.success) {
      await load()
    }
    setGenerating(false)
  }

  const handleSavePrompt = async () => {
    setSavingPrompt(true)
    const val = promptText.trim() || null
    await countryGuidesApi.updatePrompt(code, val)
    setEditingPrompt(false)
    setSavingPrompt(false)
    await load()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[var(--text-muted)]">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading…
      </div>
    )
  }

  if (!profile) {
    return <div className="text-center py-10 text-[var(--text-muted)]">Profile not found</div>
  }

  const activePrompt = profile.custom_prompt || defaultPrompt

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="mt-1 p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
            <ChevronRight size={18} className="rotate-180" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">
              {profile.country_name}
              <span className="text-sm font-normal text-[var(--text-muted)] ml-2">({profile.country_code})</span>
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <StatusBadge status={profile.status} />
              {profile.generated_at && (
                <span className="text-xs text-[var(--text-muted)]">
                  Generated {formatDate(profile.generated_at)}
                  {profile.generated_by_name && ` by ${profile.generated_by_name}`}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {generating ? 'Generating…' : profile.generated_content ? 'Regenerate' : 'Generate Guide'}
        </button>
      </div>

      {/* Prompt section */}
      <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <Edit3 size={14} />
            Generation Prompt
            <span className="text-xs font-normal text-[var(--text-muted)]">
              ({profile.custom_prompt ? 'custom' : 'default'})
            </span>
          </h3>
          <div className="flex items-center gap-2">
            {editingPrompt ? (
              <>
                <button onClick={handleSavePrompt} disabled={savingPrompt}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-green-600 text-white hover:bg-green-500 disabled:opacity-50">
                  <Check size={12} /> Save
                </button>
                <button onClick={() => setEditingPrompt(false)}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
                  <X size={12} /> Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={() => {
                  navigator.clipboard.writeText(activePrompt)
                  setPromptCopied(true)
                  setTimeout(() => setPromptCopied(false), 2000)
                }}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                  {promptCopied ? <CheckCheck size={12} className="text-green-500" /> : <Copy size={12} />}
                  {promptCopied ? 'Copied' : 'Copy'}
                </button>
                <button onClick={() => { setEditingPrompt(true); setPromptText(profile.custom_prompt || defaultPrompt) }}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                  <Edit3 size={12} /> Edit
                </button>
              </>
            )}
          </div>
        </div>

        {editingPrompt ? (
          <div className="px-4 pb-4 border-t border-[var(--border-color)] space-y-2 pt-3">
            <textarea
              value={promptText}
              onChange={e => setPromptText(e.target.value)}
              rows={20}
              className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] font-mono resize-y"
            />
            <p className="text-xs text-[var(--text-muted)]">
              Use <code className="bg-[var(--bg-tertiary)] px-1 rounded">{'{{COUNTRY}}'}</code> as a placeholder for the country name. Clear all text to revert to the default prompt.
            </p>
          </div>
        ) : (
          <div className="px-4 pb-4 border-t border-[var(--border-color)] max-h-[400px] overflow-y-auto pt-3">
            <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-mono leading-relaxed">{activePrompt}</pre>
          </div>
        )}
      </div>

      {/* Generated content */}
      {profile.generated_content && (
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setContentExpanded(!contentExpanded)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                <FileText size={16} />
                Generated Guide
                <span className="text-xs font-normal text-[var(--text-muted)]">
                  ({Math.round((profile.generated_content?.length || 0) / 1024)}KB)
                </span>
              </h3>
              <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform ${contentExpanded ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(profile.generated_content || '')
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              title="Copy guide to clipboard"
            >
              {copied ? <CheckCheck size={14} className="text-green-500" /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {contentExpanded && (
            <div className="px-4 pb-4 border-t border-[var(--border-color)] max-h-[600px] overflow-y-auto">
              {renderGuideMarkdown(profile.generated_content)}
            </div>
          )}
        </div>
      )}

      {generating && !profile.generated_content && (
        <div className="p-8 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <Loader2 size={24} className="animate-spin mx-auto mb-3 text-primary" />
          <p className="text-sm text-[var(--text-muted)]">Generating guide for {profile.country_name}…</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">This can take up to 3 minutes.</p>
        </div>
      )}
    </div>
  )
}

// ─── Add Country Modal ────────────────────────────────────────────────

// Deduplicated country list: one entry per code, preferring shorter/cleaner names
const UNIQUE_COUNTRIES: { name: string; code: string }[] = (() => {
  const byCode: Record<string, string> = {}
  for (const [name, code] of Object.entries(COUNTRY_CODES)) {
    if (!byCode[code] || name.length < byCode[code].length) byCode[code] = name
  }
  return Object.entries(byCode).map(([code, name]) => ({ name, code })).sort((a, b) => a.name.localeCompare(b.name))
})()

function AddCountryModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  const q = search.toLowerCase().trim()
  const entries = !q
    ? UNIQUE_COUNTRIES.slice(0, 30)
    : UNIQUE_COUNTRIES
        .filter(e => e.name.toLowerCase().includes(q) || e.code.toLowerCase() === q)
        .sort((a, b) => {
          const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1
          const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1
          return aStarts - bStarts || a.name.localeCompare(b.name)
        })
        .slice(0, 30)

  const handleSelect = async (name: string, code: string) => {
    setSaving(true)
    const res = await countryGuidesApi.create(code, name)
    if (res.success) {
      onAdded()
      onClose()
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--bg-primary)] rounded-xl border border-[var(--border-color)] w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
          <h3 className="font-semibold text-[var(--text-primary)]">Add Country</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
            <X size={16} />
          </button>
        </div>
        <div className="p-4">
          <input
            type="text"
            placeholder="Search countries…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] mb-3"
          />
          <div className="max-h-64 overflow-y-auto space-y-1">
            {entries.map(e => (
              <button
                key={e.code}
                onClick={() => handleSelect(e.name, e.code)}
                disabled={saving}
                className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)] transition-colors disabled:opacity-50"
              >
                <span>{e.name}</span>
                <span className="text-xs text-[var(--text-muted)] font-mono">{e.code}</span>
              </button>
            ))}
            {entries.length === 0 && (
              <p className="text-center text-sm text-[var(--text-muted)] py-4">No matching countries</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────

export function CountryGuides() {
  const [profiles, setProfiles] = useState<CountryGuideProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCode, setSelectedCode] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [deletingCode, setDeletingCode] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await countryGuidesApi.list()
    if (res.success && res.data) {
      setProfiles(res.data)
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const handleDelete = async (code: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('Delete this country profile and its generated guide?')) return
    setDeletingCode(code)
    await countryGuidesApi.delete(code)
    await load()
    setDeletingCode(null)
  }

  if (selectedCode) {
    return (
      <div className="max-w-5xl mx-auto">
        <CountryGuideDetail code={selectedCode} onBack={() => { setSelectedCode(null); load() }} />
      </div>
    )
  }

  const generated = profiles.filter(p => p.status === 'generated').length
  const pending = profiles.filter(p => p.status === 'pending' || p.status === 'error').length

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-3">
            <Globe size={20} />
            Country Verification Guides
          </h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            AI-generated verification guides covering ID documents, naming conventions, and data-matching advice per country.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 transition-colors"
        >
          <Plus size={14} />
          Add Country
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-[var(--text-primary)]">{profiles.length}</p>
          <p className="text-xs text-[var(--text-muted)]">Countries</p>
        </div>
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{generated}</p>
          <p className="text-xs text-[var(--text-muted)]">Guides Generated</p>
        </div>
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{pending}</p>
          <p className="text-xs text-[var(--text-muted)]">Pending</p>
        </div>
      </div>

      {/* Country List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading…
        </div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-12 rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <Globe size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
          <p className="text-sm text-[var(--text-muted)]">No country profiles yet.</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Click "Add Country" to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {profiles.map(p => (
            <div
              key={p.country_code}
              className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => setSelectedCode(p.country_code)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <Globe size={16} className="text-primary flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                      {p.country_name}
                    </h3>
                    <span className="text-xs text-[var(--text-muted)] font-mono">{p.country_code}</span>
                    <StatusBadge status={p.status} />
                    {p.has_custom_prompt ? (
                      <span className="text-xs text-[var(--text-muted)]">Custom prompt</span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-4 ml-7 text-xs text-[var(--text-muted)]">
                    {p.generated_at && <span>Generated {formatDate(p.generated_at)}</span>}
                    {p.generated_by_name && <span>by {p.generated_by_name}</span>}
                    {p.content_length ? <span>{Math.round(p.content_length / 1024)}KB</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleDelete(p.country_code, e)}
                    disabled={deletingCode === p.country_code}
                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-600 transition-colors"
                    title="Delete profile"
                  >
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={18} className="text-[var(--text-muted)] flex-shrink-0" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddCountryModal onClose={() => setShowAdd(false)} onAdded={load} />}
    </div>
  )
}
